import { makeAutoObservable, runInAction } from 'mobx';

import { messageFor } from '@/services/errors';
import { voltrService } from '@/services/voltr';
import { dealsStore } from '@/stores/deals.store';
import type { VoltrShipping, VoltrSocial } from '@/types/voltr';

/**
 * MarketPryce Social (Voltr) order flow: the brand's catalog for one deal and
 * the product order (the video upload lives in voltrUpload.store). The Action the server returns is handed to dealsStore so
 * the deal page's stepper picks it up.
 */
class VoltrStore {
  social: VoltrSocial | null = null;
  socialSlug: string | null = null;
  loading = false;
  loadError: string | null = null;
  submitting = false;
  submitError: string | null = null;
  /** Bumped by reset(): an answer to a request the previous user made changes nothing. */
  private epoch = 0;
  /** The latest load(); only its answer is written (an older one for the same deal is stale). */
  private loadSeq = 0;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Resolves true when /social for `slug` is loaded. Only the latest load in
   * this session writes: a slower response for a deal the user already left,
   * an older one for the same deal, or one landing after logout changes
   * nothing (not `loading`, not `loadError`). Its Action is a snapshot, so
   * one written since (an upload, a remove, an order) wins over it.
   */
  async load(slug: string): Promise<boolean> {
    if (this.socialSlug !== slug) {
      this.social = null;
      this.socialSlug = slug;
    }
    this.loading = true;
    this.loadError = null;
    const epoch = this.epoch;
    const seq = (this.loadSeq += 1);
    const mark = dealsStore.actionMark();
    const current = () => this.epoch === epoch && this.loadSeq === seq;
    try {
      const social = await voltrService.social(slug);
      runInAction(() => {
        if (!current()) return;
        this.social = social;
        this.loading = false;
        if (social.action) dealsStore.applyActionSince(mark, social.action, { snapshot: true });
      });
      return current();
    } catch (err) {
      runInAction(() => {
        if (!current()) return;
        this.loading = false;
        this.loadError = messageFor(err, "Couldn't load this deal from the brand. Try again.");
      });
      return false;
    }
  }

  /** Returns true when the order went through. An answer landing after logout is dropped. */
  async order(actionId: number, productId: string, shipping: VoltrShipping): Promise<boolean> {
    this.submitting = true;
    this.submitError = null;
    const epoch = this.epoch;
    const mark = dealsStore.actionMark();
    try {
      const action = await voltrService.requestSample(actionId, productId, shipping);
      if (this.epoch !== epoch || !dealsStore.applyActionSince(mark, action)) return false;
      // The deal page now shows only the ordered product, which /social names.
      if (this.socialSlug) void this.load(this.socialSlug);
      return true;
    } catch (err) {
      runInAction(() => {
        if (this.epoch === epoch) this.submitError = messageFor(err, "That didn't go through. Try again in a moment.");
      });
      return false;
    } finally {
      runInAction(() => {
        if (this.epoch === epoch) this.submitting = false;
      });
    }
  }

  reset(): void {
    this.epoch += 1;
    this.social = null;
    this.socialSlug = null;
    this.loading = false;
    this.loadError = null;
    this.submitting = false;
    this.submitError = null;
  }
}

export const voltrStore = new VoltrStore();
